import { action, computed, makeObservable, observable, runInAction } from 'mobx';

import { DropdownItem } from '../../components/input';
import { Store } from '../../store/store';
import { StoreNode } from '../../store';
import { DocumentUploadFileState } from './documentUploadFileState';
import { DocumentUploadWorkflow } from './documentUploadGuidedTrainingState';
import { WarrantUploadState } from './warrantUploadState';

export type DocumentUploadPageParams = {
  teamId?: string;
};

const MY_UPLOADS_VALUE = 'my-uploads';

export class DocumentUploadPageState
  extends StoreNode {

  constructor(store: Store) {
    super(store);
    makeObservable(this);

    this.fileComponent = new DocumentUploadFileState(this.store, {
      teamId: () => this.teamId,
    });
    this.warrantComponent = new WarrantUploadState(this.store, {
      teamId: () => this.teamId,
    });
  }

  readonly fileComponent: DocumentUploadFileState;
  readonly warrantComponent: WarrantUploadState;

  @observable selectedTeamId: string | null = null;
  @observable mode: DocumentUploadWorkflow = 'warrant';

  private mountVersion = 0;

  @computed
  get teamId(): string | undefined {
    return this.selectedTeamId ?? undefined;
  }

  @computed
  get team() {
    return this.teamId ? this.store.teamManager.getTeam(this.teamId) : null;
  }

  @computed
  get destinationItems(): DropdownItem[] {
    return [
      { value: MY_UPLOADS_VALUE, label: 'My Uploads' },
      ...this.store.teamManager.teamsSortedByName
        .filter(candidate => candidate.hasPermission('CreateJob'))
        .map(candidate => ({ value: candidate.id, label: candidate.displayName || candidate.name })),
    ];
  }

  @computed
  get destinationValue(): string {
    return this.selectedTeamId ?? MY_UPLOADS_VALUE;
  }

  @computed
  get activeUploadState() {
    return this.mode === 'warrant' ? this.warrantComponent : this.fileComponent;
  }

  @computed
  get showStaging(): boolean {
    return this.mode === 'warrant'
      ? this.warrantComponent.showStagingForm
      : (this.fileComponent.showStagingForm || this.fileComponent.showProgressBox);
  }

  @computed
  get stagedCount(): number {
    return this.mode === 'warrant'
      ? this.warrantComponent.stagedItems.length
      : this.fileComponent.stagedItems.length;
  }

  @action
  async mounted(params: DocumentUploadPageParams) {
    const currentMountVersion = ++this.mountVersion;
    this.selectedTeamId = params.teamId ?? null;
    this.mode = this.store.documentUploadGuidedTraining.getDefaultWorkflow();

    await this.store.teamManager.apiEnsureTeams();
    if (currentMountVersion !== this.mountVersion)
      return;

    if (!params.teamId) {
      const defaultLibraryId = this.resolveDefaultLibraryId();
      if (defaultLibraryId)
        runInAction(() => {
          if (currentMountVersion === this.mountVersion)
            this.selectedTeamId = defaultLibraryId;
        });
    }

    runInAction(() => {
      if (currentMountVersion === this.mountVersion)
        this.mode = this.store.documentUploadGuidedTraining.getDefaultWorkflow(this.team);
    });
    this.fileComponent.init();
    this.store.documentUploadGuidedTraining.registerUploadPage(this.guidedTrainingContext);
    this.store.documentUploadGuidedTraining.handleUploadPageMounted();
  }

  @action
  unmounted() {
    ++this.mountVersion;
    this.store.documentUploadGuidedTraining.handleUploadPageUnmounted();
    this.store.documentUploadGuidedTraining.unregisterUploadPage(this.guidedTrainingContext);
    this.fileComponent.reset();
    this.warrantComponent.reset();
    this.selectedTeamId = null;
    this.mode = this.store.documentUploadGuidedTraining.getDefaultWorkflow();
  }

  @action
  setSelectedTeamId(teamId: string | null) {
    this.selectedTeamId = teamId;
  }

  @action
  selectMode(workflow: DocumentUploadWorkflow) {
    this.mode = workflow;
    this.store.documentUploadGuidedTraining.handleWorkflowSelected(workflow);
  }

  @action
  setMode(workflow: DocumentUploadWorkflow) {
    this.mode = workflow;
  }

  handleBackButton(prevRoute: string | null) {
    if (prevRoute)
      window.history.back();
  }

  private resolveDefaultLibraryId(): string | null {
    const email = this.store.user?.email?.toLowerCase();
    if (!email)
      return null;

    return this.store.teamManager.teams.find(
      team => team.externalEmail?.toLowerCase() === email && team.hasPermission('CreateJob')
    )?.id ?? null;
  }

  private readonly guidedTrainingContext = {
    getTeamId: () => this.teamId ?? null,
    getWorkflow: () => this.mode,
    setWorkflow: (workflow: DocumentUploadWorkflow) => this.setMode(workflow),
    getTranslationState: () => this.fileComponent,
    getWarrantState: () => this.warrantComponent,
  };
}
